import API_CONFIG from '../config/api';

const TRANSCRIPTION_UNAVAILABLE_MESSAGE = "Couldn't reach transcription service - tap to retry";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isIOSDevice = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isIOS = /iphone|ipad|ipod/i.test(ua);
  const isIPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return isIOS || isIPadOS;
};

// Voice-to-Text Service using Google Cloud Speech-to-Text API
class VoiceToTextService {
  constructor() {
    this.isRecording = false;
    this.mediaRecorder = null;
    this.audioChunks = [];
    this.stream = null;
    this.recorderMimeType = this.pickMimeType();
    this.activeMimeType = '';
    this.usePcmRecorder = false;
    this.audioContext = null;
    this.sourceNode = null;
    this.processorNode = null;
    this.gainNode = null;
    this.pcmBuffers = [];
    this.pcmSampleRate = 16000;
  }

  // Check if browser supports speech recognition
  isSupported() {
    const hasMedia = navigator.mediaDevices && navigator.mediaDevices.getUserMedia;
    const hasRecorder = typeof window !== 'undefined' && window.MediaRecorder;
    const hasAudioContext = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);
    return !!(hasMedia && (hasRecorder || hasAudioContext));
  }

  pickMimeType() {
    if (typeof window === 'undefined' || !window.MediaRecorder || typeof window.MediaRecorder.isTypeSupported !== 'function') {
      return '';
    }

    const preferIOS = [
      'audio/mp4;codecs=mp4a.40.2',
      'audio/mp4',
      'audio/aac'
    ];
    const preferWebm = [
      'audio/webm;codecs=opus',
      'audio/webm'
    ];

    const isSupported = (type) => window.MediaRecorder.isTypeSupported(type);

    if (isIOSDevice()) {
      for (const type of preferIOS) {
        if (isSupported(type)) return type;
      }
      for (const type of preferWebm) {
        if (isSupported(type)) return type;
      }
      return '';
    }

    for (const type of preferWebm) {
      if (isSupported(type)) return type;
    }
    for (const type of preferIOS) {
      if (isSupported(type)) return type;
    }
    if (isSupported('audio/mpeg')) return 'audio/mpeg';
    return '';
  }

  shouldUsePcmRecording() {
    if (process.env.REACT_APP_FORCE_PCM === '1' || process.env.REACT_APP_FORCE_PCM === 'true') {
      return true;
    }
    return isIOSDevice();
  }

  getPreferredMimeType() {
    return this.recorderMimeType || '';
  }

  getActiveMimeType() {
    return this.activeMimeType || this.recorderMimeType || '';
  }

  // Start recording audio
  async startRecording() {
    if (this.isRecording) return;

    try {
      this.usePcmRecorder = this.shouldUsePcmRecording();
      this.recorderMimeType = this.pickMimeType();

      if (this.usePcmRecorder) {
        await this.startPcmRecording();
        return true;
      }

      // Request microphone access
      this.stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        } 
      });

      // Create MediaRecorder
      const options = {};
      if (this.recorderMimeType) {
        options.mimeType = this.recorderMimeType;
      }

      this.mediaRecorder = new MediaRecorder(this.stream, options);
      this.activeMimeType = this.mediaRecorder.mimeType || this.recorderMimeType || '';

      this.audioChunks = [];
      this.isRecording = true;

      // Collect audio data
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      // Start recording
      this.mediaRecorder.start();

      return true;
    } catch (error) {
      console.error('Error starting recording:', error);
      this.cleanup();
      this.activeMimeType = '';
      throw new Error('Could not access microphone. Please check permissions.');
    }
  }

  async startPcmRecording() {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) {
      throw new Error('Audio context not supported');
    }

    this.stream = await navigator.mediaDevices.getUserMedia({ 
      audio: {
        sampleRate: 16000,
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true
      } 
    });

    this.audioContext = new AudioCtx();
    if (this.audioContext.state === 'suspended') {
      try { await this.audioContext.resume(); } catch {}
    }
    this.pcmSampleRate = this.audioContext.sampleRate;
    this.sourceNode = this.audioContext.createMediaStreamSource(this.stream);
    this.processorNode = this.audioContext.createScriptProcessor(4096, 1, 1);
    this.gainNode = this.audioContext.createGain();
    this.gainNode.gain.value = 0;

    this.pcmBuffers = [];
    this.isRecording = true;
    this.activeMimeType = 'audio/wav';

    this.processorNode.onaudioprocess = (event) => {
      if (!this.isRecording) return;
      const input = event.inputBuffer.getChannelData(0);
      this.pcmBuffers.push(new Float32Array(input));
    };

    this.sourceNode.connect(this.processorNode);
    this.processorNode.connect(this.gainNode);
    this.gainNode.connect(this.audioContext.destination);
  }

  // Stop recording and return audio blob
  async stopRecording() {
    if (!this.isRecording) return null;
    if (this.usePcmRecorder) {
      return this.stopPcmRecording();
    }
    if (!this.mediaRecorder) return null;

    return new Promise((resolve, reject) => {
      this.mediaRecorder.onstop = () => {
        const mimeType = this.activeMimeType || this.mediaRecorder?.mimeType || this.recorderMimeType || 'audio/webm;codecs=opus';
        const audioBlob = new Blob(this.audioChunks, { type: mimeType });
        this.cleanup();
        resolve(audioBlob);
      };

      this.mediaRecorder.onerror = (error) => {
        this.cleanup();
        reject(error);
      };

      this.mediaRecorder.stop();
    });
  }

  async stopPcmRecording() {
    if (!this.isRecording) return null;

    this.isRecording = false;

    const buffers = this.pcmBuffers;
    const inputSampleRate = this.pcmSampleRate;

    this.cleanup();

    if (!buffers || buffers.length === 0) {
      return null;
    }

    const merged = this.mergeFloat32(buffers);
    const resampled = this.resampleBuffer(merged, inputSampleRate, 16000);
    const wavBuffer = this.encodeWav(resampled, 16000);

    return new Blob([wavBuffer], { type: 'audio/wav' });
  }

  // Clean up resources
  cleanup() {
    this.isRecording = false;
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
    if (this.processorNode) {
      try { this.processorNode.disconnect(); } catch {}
      this.processorNode = null;
    }
    if (this.sourceNode) {
      try { this.sourceNode.disconnect(); } catch {}
      this.sourceNode = null;
    }
    if (this.gainNode) {
      try { this.gainNode.disconnect(); } catch {}
      this.gainNode = null;
    }
    if (this.audioContext) {
      try { this.audioContext.close(); } catch {}
      this.audioContext = null;
    }
    this.mediaRecorder = null;
    this.audioChunks = [];
    this.activeMimeType = '';
    this.usePcmRecorder = false;
    this.pcmBuffers = [];
    this.pcmSampleRate = 16000;
  }

  // Convert audio blob to base64 for API
  async audioToBase64(audioBlob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(audioBlob);
    });
  }

  getEncodingForMime(mimeType = '') {
    const mt = mimeType.toLowerCase();
    if (mt.includes('wav')) return 'LINEAR16';
    if (mt.includes('webm')) return 'WEBM_OPUS';
    if (mt.includes('ogg')) return 'OGG_OPUS';
    if (mt.includes('mp3')) return 'MP3';
    if (mt.includes('mp4') || mt.includes('aac')) return 'MP4';
    return 'ENCODING_UNSPECIFIED';
  }

  getSpeechHints() {
    return [
      'cotton buds',
      'cotton pads',
      'cotton swabs',
      'cotton sticks',
      'nail files',
      'nail file',
      'nail varnish',
      'hair spray',
      'hairspray',
      'hairbrushes',
      'hair brushes',
      'items in a box',
      'items in a pack',
      'box of',
      'pack of',
      'two euro',
      'three euro',
      'four euro',
      'five euro'
    ];
  }

  scoreTranscript(text) {
    if (!text) return 0;
    const t = text.toLowerCase();
    let score = 0;
    if (/\d/.test(t)) score += 2;
    if (/\b(euro|euros|dollar|dollars|pound|pounds)\b/.test(t)) score += 2;
    if (/\b(two|three|four|five|six|seven|eight|nine|ten)\b/.test(t)) score += 1;
    if (/\b(cotton|nail|hair|box|pack|bottle|items)\b/.test(t)) score += 1;
    return score;
  }

  normalizeTranscript(text) {
    if (!text) return text;
    let t = text;
    t = t.replace(/\bcotton boards?\b/gi, 'cotton buds');
    t = t.replace(/\bcotton birds?\b/gi, 'cotton buds');
    t = t.replace(/\bto\s+euro(?:pe)?\b/gi, 'two euro');
    t = t.replace(/\btoo\s+euro(?:pe)?\b/gi, 'two euro');
    t = t.replace(/\b(\d+|one|two|three|four|five|six|seven|eight|nine|ten)\s+europe\b/gi, '$1 euro');
    return t;
  }

  pickBestTranscript(alternatives = []) {
    if (!Array.isArray(alternatives) || alternatives.length === 0) return '';
    const scored = alternatives
      .map((alt) => ({
        transcript: alt.transcript || '',
        score: this.scoreTranscript(alt.transcript || ''),
        confidence: typeof alt.confidence === 'number' ? alt.confidence : 0
      }))
      .sort((a, b) => (b.score - a.score) || (b.confidence - a.confidence));
    return scored[0]?.transcript || '';
  }

  // Send audio to Google Cloud Speech-to-Text API
  async transcribeAudio(audioBlob) {
    const base64Audio = await this.audioToBase64(audioBlob);
    const encoding = this.getEncodingForMime(audioBlob?.type || this.recorderMimeType || '');
    const speechHints = this.getSpeechHints();
    const url = `${API_CONFIG.FIREBASE_FUNCTIONS_URL}${API_CONFIG.ENDPOINTS.SPEECH_TO_TEXT}`;
    const body = JSON.stringify({
      audio: base64Audio,
      config: {
        encoding,
        sampleRateHertz: 16000,
        languageCode: 'en-US', // Can be made configurable
        alternativeLanguageCodes: ['en-GB', 'en-IE', 'es-ES', 'fr-FR', 'de-DE'], // Support multiple accents
        enableAutomaticPunctuation: true,
        model: 'latest_long',
        maxAlternatives: 3,
        speechContexts: speechHints.length ? [{ phrases: speechHints, boost: 15 }] : undefined
      }
    });

    const request = async () => {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body
      });

      if (!response.ok) {
        const err = new Error(`Speech-to-text API error: ${response.status}`);
        err.status = response.status;
        throw err;
      }

      return response.json();
    };

    let lastError;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const result = await request();
        if (result.results && result.results.length > 0) {
          const alternatives = result.results[0].alternatives || [];
          const picked = this.pickBestTranscript(alternatives);
          const normalized = this.normalizeTranscript(picked || alternatives[0]?.transcript || '');
          if (normalized) return normalized;
        }
        throw new Error('No speech detected');
      } catch (error) {
        lastError = error;
        const shouldRetry = attempt === 0 && this.shouldRetryTranscription(error);
        if (shouldRetry) {
          await sleep(400 * (attempt + 1));
          continue;
        }
        console.error('Error transcribing audio:', error);
        throw this.normalizeTranscriptionError(error);
      }
    }

    console.error('Error transcribing audio:', lastError);
    throw this.normalizeTranscriptionError(lastError);
  }

  shouldRetryTranscription(error) {
    const status = typeof error?.status === 'number' ? error.status : null;
    if (status === 429 || (status && status >= 500)) return true;
    const message = String(error?.message || '');
    if (error?.name === 'TypeError' && message.toLowerCase().includes('fetch')) return true;
    if (message.toLowerCase().includes('network')) return true;
    return false;
  }

  normalizeTranscriptionError(error) {
    const status = typeof error?.status === 'number' ? error.status : null;
    const message = String(error?.message || '');
    const isNetworkError = error?.name === 'TypeError' && message.toLowerCase().includes('fetch');
    const isRetryableStatus = status === 429 || (status && status >= 500);
    const isNetworkMessage = message.toLowerCase().includes('network');

    if (isNetworkError || isRetryableStatus || isNetworkMessage) {
      const wrapped = new Error(TRANSCRIPTION_UNAVAILABLE_MESSAGE);
      wrapped.name = 'TranscriptionUnavailable';
      wrapped.cause = error;
      return wrapped;
    }

    return error;
  }

  // Main method to record and transcribe
  async recordAndTranscribe() {
    try {
      await this.startRecording();
      
      // Return a promise that resolves when recording is stopped
      return new Promise((resolve, reject) => {
        this.resolveTranscription = resolve;
        this.rejectTranscription = reject;
      });
    } catch (error) {
      throw error;
    }
  }

  // Method to be called when user stops recording
  async finishRecording() {
    try {
      const audioBlob = this.usePcmRecorder
        ? await this.stopPcmRecording()
        : await this.stopRecording();
      if (!audioBlob) {
        throw new Error('No audio recorded');
      }

      const transcript = await this.transcribeAudio(audioBlob);
      
      if (this.resolveTranscription) {
        this.resolveTranscription(transcript);
      }
      
      return transcript;
    } catch (error) {
      if (this.rejectTranscription) {
        this.rejectTranscription(error);
      }
      throw error;
    }
  }

  mergeFloat32(buffers) {
    const length = buffers.reduce((sum, buf) => sum + buf.length, 0);
    const result = new Float32Array(length);
    let offset = 0;
    buffers.forEach((buf) => {
      result.set(buf, offset);
      offset += buf.length;
    });
    return result;
  }

  resampleBuffer(buffer, inputSampleRate, targetSampleRate) {
    if (inputSampleRate === targetSampleRate) return buffer;
    const sampleRateRatio = inputSampleRate / targetSampleRate;
    const newLength = Math.round(buffer.length / sampleRateRatio);
    const result = new Float32Array(newLength);
    for (let i = 0; i < newLength; i += 1) {
      const position = i * sampleRateRatio;
      const index = Math.floor(position);
      const nextIndex = Math.min(index + 1, buffer.length - 1);
      const weight = position - index;
      result[i] = buffer[index] + (buffer[nextIndex] - buffer[index]) * weight;
    }
    return result;
  }

  encodeWav(samples, sampleRate) {
    const buffer = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buffer);

    const writeString = (offset, string) => {
      for (let i = 0; i < string.length; i += 1) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    writeString(0, 'RIFF');
    view.setUint32(4, 36 + samples.length * 2, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true); // PCM
    view.setUint16(22, 1, true); // mono
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true); // byte rate
    view.setUint16(32, 2, true); // block align
    view.setUint16(34, 16, true); // bits per sample
    writeString(36, 'data');
    view.setUint32(40, samples.length * 2, true);

    let offset = 44;
    for (let i = 0; i < samples.length; i += 1) {
      let s = Math.max(-1, Math.min(1, samples[i]));
      s = s < 0 ? s * 0x8000 : s * 0x7fff;
      view.setInt16(offset, s, true);
      offset += 2;
    }

    return buffer;
  }
}

export default new VoiceToTextService();
